// Time axes. When the engine says it ordered (and gap-filled) the axis (`QueryResult.timeAxis`), charts draw the
// rows in the order they arrived and add nothing. Otherwise (older or client-made results) charts never trust row
// order for time: buckets are ordered by their value, and missing buckets inside the report's period are filled so
// a trend never implies growth or decline that did not happen.
// Pure functions over QueryResult; the renderer and the KPI share them.

import type { QueryResult, QueryResultColumn } from './types';

/** Range the server says it applied, when it sends one (QueryResult.EffectiveDateRanges). */
interface EffectiveRange { field?: string | null; from?: string | null; to?: string | null }
type ResultWithRanges = QueryResult & { effectiveDateRanges?: EffectiveRange[] | null };

const GRAINS = new Set(['day', 'week', 'month', 'quarter', 'year']);
/** Never synthesise more buckets than a chart can usefully draw. */
export const MAX_FILLED_BUCKETS = 1500;

export function isTimeColumn(column: QueryResultColumn | undefined | null): boolean {
    return !!column && column.kind === 'dimension' && (column.dataType === 'date' || (!!column.timeGrain && GRAINS.has(column.timeGrain)));
}

/** Bucket values are local calendar labels; read them as UTC so no browser time zone shifts them. */
export function timeValue(raw: unknown): number | null {
    if (raw == null || raw === '') return null;
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw.getTime();
    const text = String(raw);
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00:00Z` : /(Z|[+-]\d{2}:?\d{2})$/.test(text) || !/T\d/.test(text) ? text : `${text}Z`;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? null : ms;
}

function stableSortIndices(length: number, compare: (a: number, b: number) => number): number[] {
    return Array.from({ length }, (_, i) => i).sort((a, b) => compare(a, b) || a - b);
}

/** Reorder rows (and every array parallel to them) by `order`. */
export function permuteResult(result: QueryResult, order: number[]): QueryResult {
    if (order.every((value, index) => value === index)) return result;
    return {
        ...result,
        rows: order.map(i => result.rows[i]),
        categoryKeys: result.categoryKeys ? order.map(i => result.categoryKeys![i]) : result.categoryKeys,
        cellHtml: result.cellHtml ? order.map(i => result.cellHtml![i]) : result.cellHtml,
        recordIds: result.recordIds ? order.map(i => result.recordIds![i]) : result.recordIds,
    };
}

/** The leading time dimension, when the chart's category axis is time. */
export function timeAxis(result: QueryResult): QueryResultColumn | null {
    const first = result.columns.find(c => c.kind === 'dimension');
    return isTimeColumn(first) ? first! : null;
}

/**
 * True when the server already ordered the rows by this result's time axis (oldest first, whatever the sort) and
 * filled the gaps it meant to fill: the client draws rows in order and never re-sorts or re-fills them.
 */
export function serverOrderedTime(result: QueryResult): boolean {
    const declared = result.timeAxis;
    const axis = timeAxis(result);
    return !!declared && !!axis && declared.alias === axis.alias;
}

/** Rows ordered chronologically by the time axis (missing dates last); other order is kept within a bucket. */
export function chronological(result: QueryResult): QueryResult {
    const axis = timeAxis(result);
    if (!axis || serverOrderedTime(result)) return result;
    const values = result.rows.map(row => timeValue(row[axis.alias]));
    return permuteResult(result, stableSortIndices(values.length, (a, b) => {
        const x = values[a], y = values[b];
        if (x == null || y == null) return x == null && y == null ? 0 : x == null ? 1 : -1;
        return x - y;
    }));
}

function shiftMonths(ms: number, months: number): number {
    const d = new Date(ms);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, d.getUTCDate());
}

/** Start of the bucket containing `ms`, matching the engine's date_trunc with week start and fiscal year. */
export function bucketStart(ms: number, grain: string, weekStart = 1, fiscalStartMonth = 1): number {
    const d = new Date(ms);
    const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    switch (grain) {
        case 'day': return day;
        case 'week': {
            const offset = (new Date(day).getUTCDay() - weekStart + 7) % 7;
            return day - offset * 86_400_000;
        }
        case 'month': return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
        case 'quarter': case 'year': {
            const shift = fiscalStartMonth - 1;
            const shifted = new Date(shiftMonths(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1), -shift));
            const month = grain === 'year' ? 0 : Math.floor(shifted.getUTCMonth() / 3) * 3;
            return shiftMonths(Date.UTC(shifted.getUTCFullYear(), month, 1), shift);
        }
        default: return day;
    }
}

export function nextBucket(ms: number, grain: string): number {
    switch (grain) {
        case 'day': return ms + 86_400_000;
        case 'week': return ms + 7 * 86_400_000;
        case 'month': return shiftMonths(ms, 1);
        case 'quarter': return shiftMonths(ms, 3);
        default: return shiftMonths(ms, 12);
    }
}

/** Write a synthesised bucket in the same shape as the values the server sent. */
function rawLike(sample: unknown, ms: number): string {
    const iso = new Date(ms).toISOString();
    const text = typeof sample === 'string' ? sample : '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return iso.slice(0, 10);
    if (/Z$/.test(text)) return iso.replace('.000Z', 'Z');
    if (/T\d{2}:\d{2}:\d{2}\.\d+$/.test(text)) return iso.slice(0, -1);
    return iso.slice(0, 19);
}

/** What an empty bucket holds: no records is zero for counts and sums, and no value for anything else. */
export function emptyBucketValue(measure: QueryResultColumn): number | null {
    const agg = measure.aggregate;
    return agg === 'count' || agg === 'countDistinct' || agg === 'sum' || agg === 'percentOfTotal' || measure.unit === 'count' ? 0 : null;
}

function rangeFor(result: QueryResult, axis: QueryResultColumn): { from: number | null; to: number | null } {
    const ranges = (result as ResultWithRanges).effectiveDateRanges ?? [];
    const range = ranges.find(r => r && (!r.field || !axis.field || r.field === axis.field || r.field.toLowerCase() === axis.field.toLowerCase()))
        ?? (ranges.length === 1 ? ranges[0] : undefined);
    const from = timeValue(range?.from ?? null);
    const to = timeValue(range?.to ?? null);
    return { from, to: to == null ? null : to - 1 };
}

export interface TimeBuckets {
    /** Every bucket on the axis, in order, as raw values (sent or synthesised). */
    values: unknown[];
    /** How many buckets were synthesised. */
    filled: number;
}

/**
 * The full, ordered set of buckets on the time axis: every bucket from the start of the report period (or the
 * first value) to its end (or the last value). Null when the axis is not a bucketed time dimension.
 */
export function timeBuckets(result: QueryResult): TimeBuckets | null {
    const axis = timeAxis(result);
    if (!axis || !axis.timeGrain || !GRAINS.has(axis.timeGrain)) return null;
    if (serverOrderedTime(result)) {
        // The engine's axis is final: its periods in row order, nothing synthesised.
        const seen = new Set<string>(); const values: unknown[] = [];
        for (const row of result.rows) {
            const value = row[axis.alias]; const key = JSON.stringify([typeof value, value ?? null]);
            if (!seen.has(key)) { seen.add(key); values.push(value); }
        }
        return { values, filled: 0 };
    }
    const grain = axis.timeGrain;
    const weekStart = result.calendar?.weekStart ?? 1;
    const fiscal = axis.fiscalYearStartMonth ?? result.calendar?.fiscalYearStartMonth ?? 1;
    const seen = new Map<number, unknown>();
    let sample: unknown = null;
    for (const row of result.rows) {
        const ms = timeValue(row[axis.alias]);
        if (ms == null) continue;
        sample ??= row[axis.alias];
        if (!seen.has(ms)) seen.set(ms, row[axis.alias]);
    }
    const sent = [...seen.keys()].sort((a, b) => a - b);
    const range = rangeFor(result, axis);
    const start = range.from != null ? bucketStart(range.from, grain, weekStart, fiscal) : sent[0];
    const endSource = range.to != null ? bucketStart(range.to, grain, weekStart, fiscal) : sent[sent.length - 1];
    if (start == null || endSource == null) return { values: sent.map(ms => seen.get(ms)), filled: 0 };
    // A truncated result cannot say what lies beyond its last bucket; never claim zeros there.
    const end = result.truncated && sent.length ? Math.min(endSource, sent[sent.length - 1]) : endSource;
    const first = Math.min(start, sent[0] ?? start), last = Math.max(end, sent[sent.length - 1] ?? end);
    const values: unknown[] = []; let filled = 0;
    let cursor = first; let guard = 0;
    while (cursor <= last && guard++ < MAX_FILLED_BUCKETS * 4) {
        if (seen.has(cursor)) values.push(seen.get(cursor));
        else { values.push(rawLike(sample, cursor)); filled++; }
        cursor = nextBucket(cursor, grain);
    }
    // Buckets the server sent that the generated sequence does not land on (an unexpected calendar) still show.
    const generated = new Set(values.map(v => timeValue(v)));
    for (const ms of sent) if (!generated.has(ms)) values.push(seen.get(ms));
    if (filled > MAX_FILLED_BUCKETS) return { values: sent.map(ms => seen.get(ms)), filled: 0 };
    values.sort((a, b) => (timeValue(a) ?? 0) - (timeValue(b) ?? 0));
    return { values, filled };
}

/**
 * A single-group time result with every bucket present and in order. Synthesised rows carry the empty-bucket
 * value for each measure and no category key, so they never offer a drill.
 */
export function fillTimeGaps(result: QueryResult): QueryResult {
    if (serverOrderedTime(result)) return result;
    const ordered = chronological(result);
    const dims = ordered.columns.filter(c => c.kind === 'dimension');
    if (dims.length !== 1) return ordered;
    const buckets = timeBuckets(ordered);
    if (!buckets || buckets.filled === 0) return ordered;
    const axis = dims[0];
    const byTime = new Map<number, number>();
    ordered.rows.forEach((row, index) => { const ms = timeValue(row[axis.alias]); if (ms != null && !byTime.has(ms)) byTime.set(ms, index); });
    const measures = ordered.columns.filter(c => c.kind === 'measure');
    const rows: Array<Record<string, unknown>> = []; const keys: string[] = []; const html: Array<Record<string, string>> = [];
    for (const value of buckets.values) {
        const index = byTime.get(timeValue(value)!);
        if (index != null) {
            rows.push(ordered.rows[index]); keys.push(ordered.categoryKeys?.[index] ?? ''); html.push(ordered.cellHtml?.[index] ?? {});
        } else {
            const row: Record<string, unknown> = { [axis.alias]: value };
            for (const m of measures) row[m.alias] = emptyBucketValue(m);
            rows.push(row); keys.push(''); html.push({});
        }
    }
    // Rows with a missing date stay at the end.
    ordered.rows.forEach((row, index) => {
        if (timeValue(row[axis.alias]) == null) { rows.push(row); keys.push(ordered.categoryKeys?.[index] ?? ''); html.push(ordered.cellHtml?.[index] ?? {}); }
    });
    return { ...ordered, rows, rowCount: rows.length,
        categoryKeys: ordered.categoryKeys ? keys : undefined, cellHtml: ordered.cellHtml ? html : ordered.cellHtml };
}

/** Whether a time axis crosses a year boundary, so day and week labels need the year. */
export function spansYears(values: unknown[]): boolean {
    let year: number | null = null;
    for (const value of values) {
        const ms = timeValue(value);
        if (ms == null) continue;
        const y = new Date(ms).getUTCFullYear();
        if (year == null) year = y; else if (y !== year) return true;
    }
    return false;
}
